import { useState, useMemo, useEffect, useCallback } from "react"
import styles from "./tasks-page.module.css"
import { getBrowserSupabaseClient } from "@/lib/supabase/client"
import { LoadingSkeleton } from "@/client/components/common/loading-skeleton"
import { useToast } from "@/client/components/common/toast"
import { canTransitionBountyStatus, type BountyStatus } from "@/domains/bounties/status-machine"
import { useAdminStore } from "@/shared/hooks/use-admin-store"
import type { Bounty } from "@/lib/supabase/types"
import {
  BOUNTY_STATUS_LABELS,
  BOUNTY_CATEGORIES,
  BOUNTY_CATEGORY_REVERSE,
  formatBBPDisplay,
  formatDeadlineCN,
} from "@/shared/config/bounty.config"
import { freezeBbpForBounty, refundBounty } from "@/lib/supabase/rpc"
import { useBidsForBounty, useSelectBidder } from "@/lib/hooks/use-bounties"
import type { BidCard, BidDetail } from "@/features/tasks/types"

// Bounty 行可能包含 bid_count（数据库有但类型未声明）
type BountyWithBidCount = Bounty & { bid_count?: number }

type TabType = "all" | "pending" | "active" | "completed"

// 分类列表（下拉选项使用中文标签）
const categoryOptions = Object.values(BOUNTY_CATEGORIES)
const filterCategories = ["全部", ...categoryOptions]

export default function TasksPage() {
  const toast = useToast()
  const { admin } = useAdminStore()
  const [activeTab, setActiveTab] = useState<TabType>("all")
  const [searchKeyword, setSearchKeyword] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("全部")
  const [showModal, setShowModal] = useState(false)
  const [modalMode, setModalMode] = useState<"create" | "edit" | "view">("create")
  const [currentTask, setCurrentTask] = useState<Bounty | null>(null)
  const [tasks, setTasks] = useState<Bounty[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    title: "",
    category: "",
    reward_usdc: "",
    deadline: "",
    description: "",
    tech_tags: "",
    delivery_standard: "",
  })

  // 竞标管理状态
  const [showBidsModal, setShowBidsModal] = useState(false)
  const [bidsTargetTask, setBidsTargetTask] = useState<Bounty | null>(null)
  const [expandedBidId, setExpandedBidId] = useState<string | null>(null)
  const [bidDetail, setBidDetail] = useState<BidDetail | null>(null)
  const [isLoadingBidDetail, setIsLoadingBidDetail] = useState(false)

  const { data: bids = [], isLoading: isLoadingBids } = useBidsForBounty(bidsTargetTask?.id)
  const selectBidderMutation = useSelectBidder()

  // 获取任务数据
  const fetchTasks = useCallback(async () => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase) return

    setIsLoading(true)
    try {
      const { data, error } = await supabase
        .from("bounties")
        .select("*")
        .order("created_at", { ascending: false })

      if (error) throw error
      setTasks(data || [])
    } catch {
      toast.error("获取任务数据失败")
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTasks()
  }, [fetchTasks])

  // 计算统计数据
  const stats = useMemo(() => {
    return {
      total: tasks.length,
      active: tasks.filter((t) => t.status === "in_progress" || t.status === "reviewing").length,
      pending: tasks.filter((t) => t.status === "bidding" || t.status === "drafting").length,
      completed: tasks.filter((t) => t.status === "completed").length,
    }
  }, [tasks])

  // 过滤任务列表
  const filteredTasks = useMemo(() => {
    return tasks.filter((task) => {
      // 标签页过滤
      if (activeTab === "pending" && task.status !== "drafting" && task.status !== "bidding") return false
      if (activeTab === "active" && task.status !== "in_progress" && task.status !== "reviewing") return false
      if (activeTab === "completed" && task.status !== "completed" && task.status !== "cancelled") return false

      // 分类过滤
      if (selectedCategory !== "全部") {
        const taskCategory = BOUNTY_CATEGORIES[task.category || ""] || task.category
        if (taskCategory !== selectedCategory) return false
      }

      // 搜索过滤
      if (
        searchKeyword &&
        !task.title?.toLowerCase().includes(searchKeyword.toLowerCase())
      ) {
        return false
      }

      return true
    })
  }, [tasks, activeTab, selectedCategory, searchKeyword])

  const handleOpenCreate = () => {
    setModalMode("create")
    setFormData({
      title: "",
      category: "",
      reward_usdc: "",
      deadline: "",
      description: "",
      tech_tags: "",
      delivery_standard: "",
    })
    setCurrentTask(null)
    setShowModal(true)
  }

  const handleOpenEdit = (task: Bounty) => {
    setModalMode("edit")
    setCurrentTask(task)
    // 编辑时：将 DB key 转回中文标签用于表单显示
    const categoryLabel = BOUNTY_CATEGORIES[task.category || ""] || task.category || ""
    setFormData({
      title: task.title || "",
      category: categoryLabel,
      reward_usdc: task.reward_usdc?.toString() || "",
      deadline: task.bidding_deadline ? task.bidding_deadline.split("T")[0] : "",
      description: task.description || "",
      tech_tags: Array.isArray(task.tech_tags) ? task.tech_tags.join(", ") : (task.tech_tags ?? ""),
      delivery_standard: task.requirements ?? "",
    })
    setShowModal(true)
  }

  const handleOpenView = (task: Bounty) => {
    setModalMode("view")
    setCurrentTask(task)
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setCurrentTask(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!admin?.id) {
      toast.error("管理员信息未加载，请刷新页面")
      return
    }

    const supabase = getBrowserSupabaseClient()
    if (!supabase) return

    setIsSubmitting(true)

    try {
      const now = new Date().toISOString()
      // 分类：将中文标签转换为 DB key
      const categoryKey = BOUNTY_CATEGORY_REVERSE[formData.category] || formData.category || null
      const rewardAmount = Number(formData.reward_usdc) || 0

      // 技术标签：逗号分隔 → 数组
      const techTags = formData.tech_tags
        .split(/[,，]/)
        .map((t) => t.trim())
        .filter(Boolean)

      const bountyData = {
        title: formData.title,
        category: categoryKey,
        reward_usdc: rewardAmount,
        deadline: formData.deadline ? new Date(formData.deadline).toISOString() : null,
        description: formData.description,
        tech_tags: techTags.length > 0 ? techTags : null,
        delivery_standard: formData.delivery_standard || null,
        publisher_id: admin.id,
        updated_at: now,
      }

      if (modalMode === "create") {
        // 1. 插入 bounty
        const { data: inserted, error: insertError } = await supabase
          .from("bounties")
          .insert({
            ...bountyData,
            status: "drafting",
            created_at: now,
          })
          .select("id")
          .single()

        if (insertError) throw insertError

        // 2. 冻结 BBP（创建托管记录）
        if (inserted && rewardAmount > 0) {
          try {
            await freezeBbpForBounty(supabase, inserted.id, admin.id, rewardAmount)
          } catch (freezeErr) {
            // BBP 冻结失败不阻塞发布，记录警告
            console.warn("BBP 冻结失败（可能余额不足）:", freezeErr)
          }
        }

        toast.success("任务发布成功")
      } else if (modalMode === "edit" && currentTask) {
        const { error: updateError } = await supabase
          .from("bounties")
          .update(bountyData)
          .eq("id", currentTask.id)
        if (updateError) throw updateError
        toast.success("任务更新成功")
      }

      handleCloseModal()
      fetchTasks()
    } catch {
      toast.error("保存失败，请重试")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAudit = async (taskId: string, nextStatus: string) => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase) return

    // 状态机校验
    const task = tasks.find((t) => t.id === taskId)
    if (!task) return
    if (!canTransitionBountyStatus(task.status as BountyStatus, nextStatus as BountyStatus)) {
      toast.error(`不允许从「${BOUNTY_STATUS_LABELS[task.status as keyof typeof BOUNTY_STATUS_LABELS] || task.status}」变更为「${BOUNTY_STATUS_LABELS[nextStatus as keyof typeof BOUNTY_STATUS_LABELS] || nextStatus}」`)
      return
    }

    try {
      const { error } = await supabase
        .from("bounties")
        .update({ status: nextStatus, updated_at: new Date().toISOString() })
        .eq("id", taskId)
      if (error) throw error
      toast.success(`任务状态已更新为${BOUNTY_STATUS_LABELS[nextStatus as keyof typeof BOUNTY_STATUS_LABELS] || nextStatus}`)
      fetchTasks()
    } catch {
      toast.error("操作失败，请重试")
    }
  }

  const handleDelete = async (taskId: string) => {
    if (!confirm("确定要删除这个任务吗？删除前会尝试退回托管 BBP。")) return

    const supabase = getBrowserSupabaseClient()
    if (!supabase) return

    try {
      // 先尝试退款 BBP（如果有托管记录）
      try {
        await refundBounty(supabase, taskId)
      } catch {
        // 退款失败不阻塞删除（可能没有托管记录）
      }

      const { error } = await supabase.from("bounties").delete().eq("id", taskId)
      if (error) throw error
      toast.success("任务已删除")
      fetchTasks()
    } catch {
      toast.error("删除失败，请重试")
    }
  }

  // ========== 竞标管理 ==========

  const handleOpenBidsModal = (task: Bounty) => {
    setBidsTargetTask(task)
    setExpandedBidId(null)
    setBidDetail(null)
    setShowBidsModal(true)
  }

  const handleCloseBidsModal = () => {
    setShowBidsModal(false)
    setBidsTargetTask(null)
    setExpandedBidId(null)
    setBidDetail(null)
  }

  const handleExpandBid = async (bid: BidCard) => {
    if (expandedBidId === bid.id) {
      setExpandedBidId(null)
      setBidDetail(null)
      return
    }

    setExpandedBidId(bid.id)
    setIsLoadingBidDetail(true)
    setBidDetail(null)

    const supabase = getBrowserSupabaseClient()
    if (!supabase) return

    try {
      const { getBidDetail } = await import("@/repositories/bounties.repository")
      const detail = await getBidDetail(supabase, bid.id)
      setBidDetail(detail)
    } catch {
      toast.error("加载方案详情失败")
    } finally {
      setIsLoadingBidDetail(false)
    }
  }

  const handleSelectBidder = async (bid: BidCard) => {
    if (!bidsTargetTask || !admin?.id) return
    if (!confirm(`确定选择「${bid.engineerNickname ?? "该工程师"}」的方案为中标方案吗？`)) return

    try {
      await selectBidderMutation.mutateAsync({
        bountyId: bidsTargetTask.id,
        bidId: bid.id,
        publisherId: admin.id,
      })
      toast.success("选标成功，任务已进入进行中状态")
      handleCloseBidsModal()
      fetchTasks()
    } catch {
      toast.error("选标失败，请重试")
    }
  }

  const getBidStatusTag = (status: string): { text: string; className: string } => {
    switch (status) {
      case "pending":
        return { text: "待审核", className: styles.bidStatusPending }
      case "shortlisted":
        return { text: "入围", className: styles.bidStatusShortlisted }
      case "selected":
        return { text: "已中标", className: styles.bidStatusSelected }
      case "rejected":
        return { text: "已拒绝", className: styles.bidStatusRejected }
      case "withdrawn":
        return { text: "已撤回", className: styles.bidStatusWithdrawn }
      default:
        return { text: status, className: "" }
    }
  }

  const getStatusClass = (status: string) => {
    switch (status) {
      case "in_progress":
      case "reviewing":
        return styles.statusActive
      case "drafting":
      case "bidding":
        return styles.statusPending
      case "completed":
        return styles.statusCompleted
      case "cancelled":
        return styles.statusCancelled
      default:
        return ""
    }
  }

  if (isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <h1 className={styles.title}>任务管理</h1>
        </div>
        <div className={styles.stats}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={styles.statCard}>
              <LoadingSkeleton variant="text" />
            </div>
          ))}
        </div>
        <div className={styles.toolbar}>
          <LoadingSkeleton variant="text" />
        </div>
        <div className={styles.taskList}>
          {[1, 2, 3].map((i) => (
            <LoadingSkeleton key={i} variant="rectangular" height={180} />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      {/* Tech Theme Background Effects */}
      <div className={styles.techGridBg} />
      <div className={styles.techScanlines} />

      {/*  头部  */}
      <div className={styles.header}>
        <h1 className={styles.title}>任务管理</h1>
        <button className={styles.addBtn} onClick={handleOpenCreate}>
          + 发布任务
        </button>
      </div>

      {/* 统计卡片 */}
      <div className={styles.stats}>
        <div className={`${styles.statCard} ${styles.statTotal}`}>
          <div className={styles.statIcon}>📋</div>
          <div className={styles.statContent}>
            <span className={styles.statValue}>{stats.total}</span>
            <span className={styles.statLabel}>总任务数</span>
          </div>
        </div>
        <div className={`${styles.statCard} ${styles.statActive}`}>
          <div className={styles.statIcon}>⚡</div>
          <div className={styles.statContent}>
            <span className={styles.statValue}>{stats.active}</span>
            <span className={styles.statLabel}>进行中</span>
          </div>
        </div>
        <div className={`${styles.statCard} ${styles.statPending}`}>
          <div className={styles.statIcon}>⏳</div>
          <div className={styles.statContent}>
            <span className={styles.statValue}>{stats.pending}</span>
            <span className={styles.statLabel}>待审核</span>
          </div>
        </div>
        <div className={`${styles.statCard} ${styles.statCompleted}`}>
          <div className={styles.statIcon}>✓</div>
          <div className={styles.statContent}>
            <span className={styles.statValue}>{stats.completed}</span>
            <span className={styles.statLabel}>已完成</span>
          </div>
        </div>
      </div>

      {/* 搜索和筛选 */}
      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <input
            type="text"
            placeholder="搜索任务名称..."
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        <div className={styles.categoryFilter}>
          {filterCategories.map((cat) => (
            <button
              key={cat}
              className={`${styles.categoryBtn} ${selectedCategory === cat ? styles.categoryActive : ""}`}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* 标签页 */}
      <div className={styles.tabs}>
        {(
          [
            { key: "all", label: "全部" },
            { key: "pending", label: "待审核" },
            { key: "active", label: "进行中" },
            { key: "completed", label: "已完成" },
          ] as const
        ).map((tab) => (
          <button
            key={tab.key}
            className={`${styles.tab} ${activeTab === tab.key ? styles.activeTab : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            {tab.key === "pending" && stats.pending > 0 && (
              <span className={styles.badge}>{stats.pending}</span>
            )}
          </button>
        ))}
      </div>

      {/* 任务列表 */}
      <div className={styles.taskList}>
        {filteredTasks.length === 0 ? (
          <div className={styles.empty}>暂无任务数据</div>
        ) : (
          filteredTasks.map((task) => (
            <div key={task.id} className={styles.taskCard}>
              <div className={styles.taskHeader}>
                <div className={styles.taskTitleRow}>
                  <h3 className={styles.taskTitle}>{task.title}</h3>
                  <span className={`${styles.status} ${getStatusClass(task.status)}`}>
                    {BOUNTY_STATUS_LABELS[task.status] || task.status}
                  </span>
                </div>
                <span className={styles.categoryTag}>{BOUNTY_CATEGORIES[task.category || ""] || task.category || "其他"}</span>
              </div>
              <p className={styles.taskDesc}>{task.description || "暂无描述"}</p>
              <div className={styles.taskMeta}>
                <span className={styles.metaItem}>
                  <span className={styles.metaIcon}>💰</span>
                  {formatBBPDisplay(task.reward_usdc)}
                </span>
                <span className={styles.metaItem}>
                  <span className={styles.metaIcon}>📅</span>
                  {formatDeadlineCN(task.bidding_deadline)}
                </span>
                {(task as BountyWithBidCount).bid_count != null && Number((task as BountyWithBidCount).bid_count) > 0 && (
                  <span className={`${styles.metaItem} ${task.status === "bidding" ? styles.metaBidHighlight : ""}`}>
                    <span className={styles.metaIcon}>📋</span>
                    {Number((task as BountyWithBidCount).bid_count)} 份方案
                  </span>
                )}
              </div>
                <div className={styles.taskActions}>
                  {/* 查看按钮 - 所有状态都显示 */}
                  <button className={styles.viewBtn} onClick={() => handleOpenView(task)} title="查看详情">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <span>查看</span>
                  </button>

                  {/* 编辑按钮 - 仅在起草中、竞标中、审核中可编辑 */}
                  {(task.status === "drafting" || task.status === "bidding" || task.status === "reviewing") && (
                    <button className={styles.editBtn} onClick={() => handleOpenEdit(task)} title="编辑任务">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span>编辑</span>
                    </button>
                  )}

                  {/* 查看竞标按钮 - 仅在竞标期、审核期显示 */}
                  {(task.status === "bidding" || task.status === "reviewing") && (
                    <button className={styles.bidsBtn} onClick={() => handleOpenBidsModal(task)} title="管理竞标">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                      </svg>
                      <span>竞标方案</span>
                    </button>
                  )}

                  {/* 审核按钮组 - 起草中/竞标中可进入进行中 */}
                  {(task.status === "drafting" || task.status === "bidding") && (
                    <button
                      className={styles.startBtn}
                      onClick={() => handleAudit(task.id, "in_progress")}
                      title="标记为进行中"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>开始</span>
                    </button>
                  )}

                  {/* 取消按钮 - 起草中、竞标中、审核中可取消 */}
                  {(task.status === "drafting" || task.status === "bidding" || task.status === "reviewing") && (
                    <button
                      className={styles.cancelBtn}
                      onClick={() => handleAudit(task.id, "cancelled")}
                      title="取消任务"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>取消</span>
                    </button>
                  )}

                  {/* 完成按钮 - 仅进行中可完成 */}
                  {task.status === "in_progress" && (
                    <button
                      className={styles.completeBtn}
                      onClick={() => handleAudit(task.id, "completed")}
                      title="标记为完成"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>完成</span>
                    </button>
                  )}

                  {/* 删除按钮 - 取消或已完成状态可删除 */}
                  {(task.status === "cancelled" || task.status === "completed") && (
                    <button
                      className={styles.deleteBtn}
                      onClick={() => handleDelete(task.id)}
                      title="删除任务"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span>删除</span>
                    </button>
                  )}
                </div>
            </div>
          ))
        )}
      </div>

      {/* 弹窗 */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={handleCloseModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                {modalMode === "create" ? "创建任务" : modalMode === "edit" ? "编辑任务" : "任务详情"}
              </h2>
              <button className={styles.closeBtn} onClick={handleCloseModal}>
                ×
              </button>
            </div>

            {modalMode === "view" && currentTask ? (
              <div className={styles.modalBody}>
                <div className={styles.detailItem}>
                  <label>任务名称</label>
                  <span>{currentTask.title}</span>
                </div>
                <div className={styles.detailItem}>
                  <label>分类</label>
                  <span>{BOUNTY_CATEGORIES[currentTask.category || ""] || currentTask.category || "其他"}</span>
                </div>
                <div className={styles.detailItem}>
                  <label>预算</label>
                  <span>{formatBBPDisplay(currentTask.reward_usdc)}</span>
                </div>
                <div className={styles.detailItem}>
                  <label>竞标截止</label>
                  <span>{formatDeadlineCN(currentTask.bidding_deadline)}</span>
                </div>
                <div className={styles.detailItem}>
                  <label>状态</label>
                  <span className={`${styles.status} ${getStatusClass(currentTask.status)}`}>
                    {BOUNTY_STATUS_LABELS[currentTask.status] || currentTask.status}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <label>描述</label>
                  <span>{currentTask.description || "暂无描述"}</span>
                </div>
              </div>
            ) : (
              <form className={styles.modalBody} onSubmit={handleSubmit}>
                <div className={styles.formGroup}>
                  <label htmlFor="title">任务名称</label>
                  <input
                    id="title"
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="请输入任务名称"
                    required
                    className={styles.input}
                  />
                </div>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="category">分类</label>
                    <select
                      id="category"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className={styles.select}
                    >
                      <option value="">请选择</option>
                      {categoryOptions.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="reward_usdc">预算 (BBP)</label>
                    <input
                      id="reward_usdc"
                      type="number"
                      value={formData.reward_usdc}
                      onChange={(e) => setFormData({ ...formData, reward_usdc: e.target.value })}
                      placeholder="请输入预算金额"
                      required
                      className={styles.input}
                    />
                  </div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="deadline">截止日期</label>
                    <input
                      id="deadline"
                      type="date"
                      value={formData.deadline}
                      onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                      className={styles.input}
                    />
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="description">任务描述</label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="请输入任务详细描述..."
                    rows={4}
                    className={styles.textarea}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="tech_tags">技术标签</label>
                  <input
                    id="tech_tags"
                    type="text"
                    value={formData.tech_tags}
                    onChange={(e) => setFormData({ ...formData, tech_tags: e.target.value })}
                    placeholder="多个标签用逗号分隔，如：React, TypeScript, Node.js"
                    className={styles.input}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="delivery_standard">交付标准</label>
                  <textarea
                    id="delivery_standard"
                    value={formData.delivery_standard}
                    onChange={(e) => setFormData({ ...formData, delivery_standard: e.target.value })}
                    placeholder="请描述交付物要求和验收标准..."
                    rows={3}
                    className={styles.textarea}
                  />
                </div>
                <div className={styles.modalFooter}>
                  <button type="button" className={styles.cancelBtn} onClick={handleCloseModal}>
                    取消
                  </button>
                  <button type="submit" className={styles.submitBtn} disabled={isSubmitting}>
                    {isSubmitting ? "提交中..." : modalMode === "create" ? "创建" : "保存"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* 竞标管理弹窗 */}
      {showBidsModal && bidsTargetTask && (
        <div className={styles.modalOverlay} onClick={handleCloseBidsModal}>
          <div className={styles.bidsModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                竞标方案 — {bidsTargetTask.title}
              </h2>
              <button className={styles.closeBtn} onClick={handleCloseBidsModal}>
                ×
              </button>
            </div>

            <div className={styles.modalBody}>
              {/* 任务摘要 */}
              <div className={styles.bidsTaskSummary}>
                <span className={`${styles.status} ${getStatusClass(bidsTargetTask.status)}`}>
                  {BOUNTY_STATUS_LABELS[bidsTargetTask.status] || bidsTargetTask.status}
                </span>
                <span className={styles.bidsTaskReward}>
                  预算: {formatBBPDisplay(bidsTargetTask.reward_usdc)}
                </span>
              </div>

              {/* 加载中 */}
              {isLoadingBids && (
                <div className={styles.bidsLoading}>
                  <LoadingSkeleton variant="rectangular" height={80} />
                  <LoadingSkeleton variant="rectangular" height={80} />
                </div>
              )}

              {/* 空状态 */}
              {!isLoadingBids && bids.length === 0 && (
                <div className={styles.bidsEmpty}>暂无竞标方案</div>
              )}

              {/* 方案列表 */}
              {!isLoadingBids && bids.length > 0 && (
                <div className={styles.bidsList}>
                  {bids.map((bid) => {
                    const statusTag = getBidStatusTag(bid.status)
                    const isExpanded = expandedBidId === bid.id
                    const canSelect = bidsTargetTask.status === "bidding"

                    return (
                      <div key={bid.id} className={styles.bidCard}>
                        {/* 方案摘要行 */}
                        <div className={styles.bidSummary} onClick={() => handleExpandBid(bid)}>
                          <div className={styles.bidHeader}>
                            <span className={styles.bidEngineer}>
                              {bid.engineerNickname ?? "未知工程师"}
                            </span>
                            <span className={`${styles.bidStatusTag} ${statusTag.className}`}>
                              {statusTag.text}
                            </span>
                          </div>
                          <p className={styles.bidProposal}>{bid.proposal}</p>
                          <div className={styles.bidMetrics}>
                            <span className={styles.bidMetric}>
                              报价: <strong>{bid.proposedReward.toLocaleString()} BBP</strong>
                            </span>
                            <span className={styles.bidMetric}>
                              工期: <strong>{bid.estimatedDays} 天</strong>
                            </span>
                            <span className={styles.bidMetric}>
                              评分: <strong>{bid.totalScore.toFixed(1)}</strong>
                            </span>
                          </div>
                          <span className={styles.bidExpandHint}>
                            {isExpanded ? "收起详情 ▲" : "展开详情 ▼"}
                          </span>
                        </div>

                        {/* 展开详情 */}
                        {isExpanded && (
                          <div className={styles.bidDetailPanel}>
                            {isLoadingBidDetail && (
                              <div className={styles.bidDetailLoading}>
                                <LoadingSkeleton variant="text" />
                                <LoadingSkeleton variant="text" />
                              </div>
                            )}
                            {bidDetail && bidDetail.id === bid.id && (
                              <>
                                {bidDetail.approach && (
                                  <div className={styles.bidDetailSection}>
                                    <h4 className={styles.bidDetailLabel}>技术路径</h4>
                                    <p className={styles.bidDetailText}>{bidDetail.approach}</p>
                                  </div>
                                )}
                                {bidDetail.relevantExperience && (
                                  <div className={styles.bidDetailSection}>
                                    <h4 className={styles.bidDetailLabel}>相关经验</h4>
                                    <p className={styles.bidDetailText}>{bidDetail.relevantExperience}</p>
                                  </div>
                                )}
                                {bidDetail.milestonePlan && (
                                  <div className={styles.bidDetailSection}>
                                    <h4 className={styles.bidDetailLabel}>里程碑计划</h4>
                                    <p className={styles.bidDetailText}>{bidDetail.milestonePlan}</p>
                                  </div>
                                )}
                                {bidDetail.portfolioLinks && bidDetail.portfolioLinks.length > 0 && (
                                  <div className={styles.bidDetailSection}>
                                    <h4 className={styles.bidDetailLabel}>作品链接</h4>
                                    <div className={styles.bidPortfolioLinks}>
                                      {bidDetail.portfolioLinks.map((link, idx) => (
                                        <a
                                          key={idx}
                                          href={link}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className={styles.bidPortfolioLink}
                                        >
                                          {link}
                                        </a>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {bidDetail.coverLetter && (
                                  <div className={styles.bidDetailSection}>
                                    <h4 className={styles.bidDetailLabel}>附言</h4>
                                    <p className={styles.bidDetailText}>{bidDetail.coverLetter}</p>
                                  </div>
                                )}

                                {/* 选标操作 */}
                                {canSelect && bid.status === "pending" && (
                                  <div className={styles.bidActions}>
                                    <button
                                      className={styles.selectBidderBtn}
                                      disabled={selectBidderMutation.isPending}
                                      onClick={() => handleSelectBidder(bid)}
                                    >
                                      {selectBidderMutation.isPending ? "处理中..." : "选为中标方案"}
                                    </button>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
